import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Toaster } from '../components/ui/sonner';
import { SportsAuthProvider, useSportsAuth, endSupportSession } from './SportsAuthContext';
import SportsLoginPage from './pages/SportsLoginPage';
import SportsRegisterPage from './pages/SportsRegisterPage';
import SportsForgotPasswordPage from './pages/SportsForgotPasswordPage';
import SportsResetPasswordPage from './pages/SportsResetPasswordPage';
import SportsDashboardPage from './pages/SportsDashboardPage';
import SportsMyCourtPage from './pages/SportsMyCourtPage';
import SportsOpenCourtWizard from './pages/SportsOpenCourtWizard';
import SportsBookingInquiriesPage from './pages/SportsBookingInquiriesPage';
import SportsCourtMonitoringPage from './pages/SportsCourtMonitoringPage';
import SportsCourtManagementPage from './pages/SportsCourtManagementPage';
import SportsSettingsPage from './pages/SportsSettingsPage';
import SportsSiteBuilderPage from './pages/SportsSiteBuilderPage';
import SportsWalkInPage from './pages/SportsWalkInPage';
import SportsCheckinPage from './pages/SportsCheckinPage';
import SportsScanPage from './pages/SportsScanPage';
import SportsSuperAdminPage, { SportsSuperAdminPlatformPage } from './pages/SportsSuperAdminPage';
import SportsSuperAdminTenantDetailPage from './pages/SportsSuperAdminTenantDetailPage';
import SportsTournamentsPage from './pages/SportsTournamentsPage';
import SportsTournamentDetailPage from './pages/SportsTournamentDetailPage';
import SportsOpenPlayWatchPage from './pages/SportsOpenPlayWatchPage';
import SportsRsvpPage from './pages/SportsRsvpPage';
import SportsOpenPlayRsvpPage from './pages/SportsOpenPlayRsvpPage';
import SportsVouchersPage from './pages/SportsVouchersPage';
import SportsOpenPlayWizardPage from './pages/SportsOpenPlayWizardPage';
import SportsOpenPlayDetailPage from './pages/SportsOpenPlayDetailPage';
import SportsOpenPlayPlayerPage from './pages/SportsOpenPlayPlayerPage';
import SportsScorerPage from './pages/SportsScorerPage';
import SportsCourtScorerPage from './pages/SportsCourtScorerPage';
import SportsBillingPage from './pages/SportsBillingPage';
import SportsOrganizerPage from './pages/SportsOrganizerPage';
import SportsMoneyPage from './pages/SportsMoneyPage';
import SportsKioskFleetPage from './pages/SportsKioskFleetPage';
import SportsKioskDevicePage from './pages/SportsKioskDevicePage';
import SportsKioskLivePage from './pages/SportsKioskLivePage';
import SportsKioskSalesPage from './pages/SportsKioskSalesPage';
import SportsKioskReviewPage from './pages/SportsKioskReviewPage';
import SportsKioskVouchersPage from './pages/SportsKioskVouchersPage';
import SportsBallMachinePage from './pages/SportsBallMachinePage';

// Fixed banner shown ONLY in the super admin's own tab during a support
// session (impersonation). Tenants never see it — it renders off supportSession
// state, which only exists when this tab arrived via #support_token.
function SupportSessionBanner() {
  const { organization, supportSession } = useSportsAuth();
  if (!supportSession) return null;
  const hhmm = supportSession.expiresAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 9999, display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: 12, padding: '7px 14px',
      background: '#FFB300', color: '#000', fontSize: 12, fontWeight: 700,
    }}>
      <span>
        SUPPORT SESSION — viewing as {organization?.name || 'tenant'} · expires {hhmm}
      </span>
      <button onClick={endSupportSession}
        style={{ background: '#000', color: '#FFB300', border: 'none', borderRadius: 6,
                 padding: '3px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
        Exit
      </button>
    </div>
  );
}

function SportsProtected({ children }) {
  const { user, loading } = useSportsAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-[#737373]">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/sports/login" replace />;
  return children;
}

function P({ children }) {
  return (
    <SportsProtected>
      <SupportSessionBanner />
      {children}
    </SportsProtected>
  );
}

export default function SportsApp() {
  useEffect(() => {
    document.title = 'AgriBooks Sports — Facility Booking & Management';
    return () => { document.title = 'AgriBooks | Smart Business Management System'; };
  }, []);

  return (
    <SportsAuthProvider>
      <Routes>
        <Route path="" element={<Navigate to="/sports/login" replace />} />
        <Route path="login" element={<SportsLoginPage />} />
        <Route path="register" element={<SportsRegisterPage />} />
        <Route path="forgot-password" element={<SportsForgotPasswordPage />} />
        <Route path="reset-password" element={<SportsResetPasswordPage />} />
        <Route path="dashboard" element={<P><SportsDashboardPage /></P>} />
        <Route path="courts" element={<P><SportsMyCourtPage /></P>} />
        <Route path="courts/new" element={<P><SportsOpenCourtWizard /></P>} />
        <Route path="courts/:id/edit" element={<P><SportsOpenCourtWizard /></P>} />
        <Route path="bookings" element={<P><SportsBookingInquiriesPage /></P>} />
        <Route path="walk-in" element={<P><SportsWalkInPage /></P>} />
        <Route path="money" element={<P><SportsMoneyPage /></P>} />
        <Route path="vouchers" element={<P><SportsVouchersPage /></P>} />
        {/* K6 P2: the tenant's read-only ball-machine view (shown in the nav only when a kiosk is linked) */}
        <Route path="ball-machine" element={<P><SportsBallMachinePage /></P>} />
        <Route path="monitoring" element={<P><SportsCourtMonitoringPage /></P>} />
        <Route path="live" element={<P><SportsCourtManagementPage /></P>} />
        <Route path="settings" element={<P><SportsSettingsPage /></P>} />
        <Route path="billing" element={<P><SportsBillingPage /></P>} />
        <Route path="site-builder" element={<P><SportsSiteBuilderPage /></P>} />
        <Route path="tournaments" element={<P><SportsTournamentsPage /></P>} />
        <Route path="tournaments/:id" element={<P><SportsTournamentDetailPage /></P>} />
        <Route path="open-play" element={<Navigate to="/sports/tournaments" replace />} />
        <Route path="open-play/new" element={<P><SportsOpenPlayWizardPage /></P>} />
        <Route path="open-play/:id" element={<P><SportsOpenPlayDetailPage /></P>} />
        <Route path="open-play/:id/edit" element={<P><SportsOpenPlayWizardPage /></P>} />
        {/* Public routes — no auth needed */}
        <Route path="checkin/:bookingId" element={<SportsCheckinPage />} />
        <Route path="scan" element={<SportsScanPage />} />
        <Route path="scan/:slug" element={<SportsScanPage />} />
        <Route path="score/court/:courtToken" element={<SportsCourtScorerPage />} />
        <Route path="score/:qrToken" element={<SportsScorerPage />} />
        <Route path="open-play/watch/:qrToken" element={<SportsOpenPlayWatchPage />} />
        <Route path="rsvp/:token" element={<SportsRsvpPage />} />
        <Route path="open-play-rsvp/:token" element={<SportsOpenPlayRsvpPage />} />
        {/* Open Play player self-service page (public, token-based) */}
        <Route path="open-play/player/:token" element={<SportsOpenPlayPlayerPage />} />
        {/* Organizer desk — PIN-gated event mini-POS (public link, no account) */}
        <Route path="organizer/:tid/:token" element={<SportsOrganizerPage />} />
        {/* Sports Super Admin — uses Retail agripos_token, no SportsProtected.
            Area routes: /tenants (index) + /platform (settings facets).
            The bare /super-admin URL is a permanent bookmark — keep redirecting. */}
        <Route path="super-admin" element={<Navigate to="/sports/super-admin/tenants" replace />} />
        <Route path="super-admin/tenants" element={<SportsSuperAdminPage />} />
        <Route path="super-admin/tenants/:orgId" element={<SportsSuperAdminTenantDetailPage />} />
        <Route path="super-admin/platform" element={<SportsSuperAdminPlatformPage />} />
        {/* K6 kiosk fleet — the machine owner's devices live in super admin (retail token). */}
        <Route path="super-admin/devices" element={<SportsKioskFleetPage />} />
        <Route path="super-admin/devices/:id" element={<SportsKioskDevicePage />} />
        {/* K8 P2.5 P4: every machine in use right now, across all courts — a board, not a table. */}
        <Route path="super-admin/live" element={<SportsKioskLivePage />} />
        <Route path="super-admin/sales" element={<SportsKioskSalesPage />} />
        <Route path="super-admin/review" element={<SportsKioskReviewPage />} />
        <Route path="super-admin/vouchers" element={<SportsKioskVouchersPage />} />
        <Route path="kiosk" element={<Navigate to="/sports/super-admin/devices" replace />} />
        <Route path="*" element={<Navigate to="/sports/login" replace />} />
      </Routes>
      <Toaster position="bottom-right" richColors theme="dark" />
    </SportsAuthProvider>
  );
}
